import { Button, Card, Checkbox, Col, Input, Row, Space, Typography } from 'antd'
import Popconfirm from '../components/Popconfirm'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { renderBoldMarkup } from './milwaukeeAsiaEnglish'

const { Text, Paragraph } = Typography
const { TextArea } = Input

const isWholeNumberInput = value => value === '' || /^\d+$/.test(value)

const CHECKBOX_FLOW_DATA_KEY = 'mil_asia_checkbox_flow'
const CHECKBOX_TEMPLATE_CODES = new Set([
  'milwaukee_asia_china',
  'milwaukee_asia_indonesia',
  'milwaukee_asia_malaysia_singapore'
])

const valueToOption = value => ({ label: value, value })

export function isMilAsiaCheckboxFlow (appraisal, formConfig = {}) {
  const templateCode = appraisal?.session_template || appraisal?.session_business_unit || ''
  return CHECKBOX_TEMPLATE_CODES.has(templateCode) && formConfig?.checkbox_flow?.enabled === true
}

export function checkboxFlowData (formData = {}) {
  return formData[CHECKBOX_FLOW_DATA_KEY] || {}
}

export function setCheckboxFlowData (setFormData, updater) {
  setFormData(prev => {
    const current = checkboxFlowData(prev)
    const next = typeof updater === 'function' ? updater(current) : updater
    return {
      ...prev,
      [CHECKBOX_FLOW_DATA_KEY]: {
        ...current,
        ...next
      }
    }
  })
}

export function checkboxRatingGuideSource (appraisal, formConfig = {}) {
  return formConfig?.rating_scale_guide ? { ...appraisal, rating_scale_guide: formConfig.rating_scale_guide } : appraisal
}

export function getOverallCheckboxRating (formData = {}) {
  return checkboxFlowData(formData).overall_performance_rating || ''
}

export function hasRequiredOverallCheckboxRating (formData = {}, formConfig = {}) {
  const overallConfig = formConfig?.checkbox_flow?.overall_performance_rating
  return !overallConfig?.required || Boolean(getOverallCheckboxRating(formData))
}

export function hasObjectiveText (objective) {
  return Boolean((objective?.objective || '').trim())
}

export function nextYearObjectives (formData = {}) {
  return Array.isArray(formData.next_year_objectives) ? formData.next_year_objectives : []
}

export function OverallPerformanceRatingCheckboxes ({ formData, formConfig, disabled, onChange }) {
  const overallConfig = formConfig?.checkbox_flow?.overall_performance_rating
  const options = overallConfig?.options || []
  if (!options.length) return null

  const selectedValue = getOverallCheckboxRating(formData)
  const selectedValues = selectedValue ? [selectedValue] : []
  const handleChange = values => {
    if (!onChange) return
    const nextValue = values.length ? values[values.length - 1] : ''
    onChange(nextValue)
  }

  return (
    <Card className='tti-appraisal-section-card' title={<Text strong>{overallConfig.title || 'Overall Performance Rating'}</Text>} style={{ marginBottom: 24 }}>
      {overallConfig.description && (
        <Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>
          {overallConfig.description}
        </Text>
      )}
      <Checkbox.Group value={selectedValues} onChange={handleChange} disabled={disabled} style={{ width: '100%' }}>
        <Space direction='vertical' size='small' style={{ width: '100%' }}>
          {options.map(option => (
            <Checkbox key={option.value} value={option.value}>
              <Text strong>{option.label}</Text>
              {option.description && <Text type='secondary'> - {option.description}</Text>}
            </Checkbox>
          ))}
        </Space>
      </Checkbox.Group>
      {!selectedValue && disabled && (
        <Text type='secondary' style={{ display: 'block', marginTop: 8 }}>Not selected</Text>
      )}
    </Card>
  )
}

export function CultureValuesCheckboxes ({ formData, formConfig, disabled, onChange }) {
  const cultureConfig = formConfig?.checkbox_flow?.culture_values
  const sections = cultureConfig?.sections || []
  if (!cultureConfig?.enabled || !sections.length) return null

  const data = checkboxFlowData(formData)
  const selectedValues = data.culture_values || {}
  const selectedFocus = data.culture_value_focus || ''
  const explanation = data.culture_value_explanation || ''
  const focusOptions = sections.map(section => ({ label: section.title, value: section.key }))

  const updateCultureGroup = (sectionKey, groupKey, values) => {
    if (!onChange) return
    onChange({
      culture_values: {
        ...selectedValues,
        [sectionKey]: {
          ...(selectedValues[sectionKey] || {}),
          [groupKey]: values
        }
      }
    })
  }

  return (
    <Card className='tti-appraisal-section-card' title={<Text strong>{cultureConfig.title || 'Culture and Values Competency'}</Text>} style={{ marginBottom: 24 }}>
      {cultureConfig.description && (
        <Text type='secondary' style={{ display: 'block', marginBottom: 16 }}>
          {cultureConfig.description}
        </Text>
      )}
      <Space direction='vertical' size='middle' style={{ width: '100%' }}>
        {sections.map((section, index) => (
          <Card key={section.key} size='small' type='inner' title={`${index + 1}. ${section.title}`}>
            <Row gutter={[16, 12]}>
              <Col span={12}>
                <Paragraph strong style={{ marginBottom: 8 }}>Basic Competencies</Paragraph>
                <Checkbox.Group
                  value={selectedValues[section.key]?.basic || []}
                  options={(section.basic || []).map(valueToOption)}
                  onChange={values => updateCultureGroup(section.key, 'basic', values)}
                  disabled={disabled}
                  style={{ display: 'grid', gap: 6 }}
                />
              </Col>
              <Col span={12}>
                <Paragraph strong style={{ marginBottom: 8 }}>Leadership Competencies</Paragraph>
                <Checkbox.Group
                  value={selectedValues[section.key]?.leadership || []}
                  options={(section.leadership || []).map(valueToOption)}
                  onChange={values => updateCultureGroup(section.key, 'leadership', values)}
                  disabled={disabled}
                  style={{ display: 'grid', gap: 6 }}
                />
              </Col>
            </Row>
          </Card>
        ))}

        <Card size='small' type='inner' title={cultureConfig.focus_title || 'Which value do you need to work on the most?'}>
          <Checkbox.Group
            value={selectedFocus ? [selectedFocus] : []}
            onChange={values => onChange && onChange({ culture_value_focus: values.length ? values[values.length - 1] : '' })}
            disabled={disabled}
            style={{ width: '100%', marginBottom: 12 }}
          >
            <Space direction='vertical'>
              {focusOptions.map(option => (
                <Checkbox key={option.value} value={option.value}>{option.label}</Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>Explanation</Text>
          <TextArea
            rows={3}
            value={explanation}
            onChange={event => onChange && onChange({ culture_value_explanation: event.target.value })}
            disabled={disabled}
            placeholder='Explanation...'
          />
        </Card>
      </Space>
    </Card>
  )
}

export function NextYearObjectivesCard ({ formData, formConfig, disabled, onChange }) {
  const nextYearConfig = formConfig?.checkbox_flow?.next_year_objectives || {}
  if (!nextYearConfig.enabled) return null

  const objectives = nextYearObjectives(formData)
  const visibleObjectives = disabled ? objectives.filter(hasObjectiveText) : (objectives.length ? objectives : Array.from({ length: 3 }, () => ({ priority: '', objective: '' })))
  const totalPriority = visibleObjectives.reduce((sum, objective) => sum + (parseFloat(objective.priority) || 0), 0)
  const updateObjective = (index, field, value) => {
    if (!onChange) return
    onChange(visibleObjectives.map((objective, rowIndex) => rowIndex === index ? { ...objective, [field]: value } : objective))
  }
  const removeObjective = index => {
    if (!onChange) return
    onChange(visibleObjectives.filter((_, rowIndex) => rowIndex !== index))
  }

  return (
    <Card
      className='tti-appraisal-section-card'
      title={<Text strong style={{ whiteSpace: 'pre-line' }}>{nextYearConfig.title || "Setting Next Year's Objectives"}</Text>}
      style={{ marginBottom: 24 }}
    >
      <Text type='secondary' style={{ display: 'block', marginBottom: 16, whiteSpace: 'pre-line' }}>
        {nextYearConfig.description || "The next year's objectives should be determined by the manager and employee."}
      </Text>
      {nextYearConfig.objectives_note && (
        <Text type='secondary' style={{ display: 'block', marginBottom: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0', whiteSpace: 'pre-line' }}>
          {renderBoldMarkup(nextYearConfig.objectives_note)}
        </Text>
      )}
      <div style={{ marginBottom: 12, borderBottom: '2px solid #d9d9d9' }}>
        <Row gutter={8}>
          <Col span={4}><div style={{ padding: '8px 4px', fontWeight: 600, textAlign: 'center', background: 'var(--neutral-grey0)', fontSize: 14, whiteSpace: 'pre-line' }}>{nextYearConfig.priority_label || 'Weighting (%)'}</div></Col>
          <Col span={20}><div style={{ padding: '8px 4px', fontWeight: 600, textAlign: 'center', background: 'var(--neutral-grey0)', fontSize: 14, whiteSpace: 'pre-line' }}>{nextYearConfig.objective_label || 'Annual Performance Objectives'}</div></Col>
        </Row>
      </div>
      {visibleObjectives.length
        ? visibleObjectives.map((objective, index) => (
          <div key={index} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: index < visibleObjectives.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
            <Row gutter={8} align='top'>
              <Col span={4}>
                <Input
                  size='small'
                  type='number'
                  min={0}
                  max={100}
                  step={1}
                  value={objective.priority}
                  onChange={event => {
                    if (isWholeNumberInput(event.target.value)) updateObjective(index, 'priority', event.target.value)
                  }}
                  disabled={disabled}
                  suffix='%'
                />
              </Col>
              <Col span={20}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <TextArea
                    rows={2}
                    value={objective.objective}
                    onChange={event => updateObjective(index, 'objective', event.target.value)}
                    disabled={disabled}
                    placeholder='Describe objective...'
                    style={{ fontSize: 14, flex: 1 }}
                  />
                  {!disabled && visibleObjectives.length > 1 && (
                    <Popconfirm title='Remove this objective?' onConfirm={() => removeObjective(index)} okText='Remove' okButtonProps={{ danger: true }}>
                      <Button type='text' danger icon={<DeleteOutlined />} aria-label={`Remove next-year objective ${index + 1}`} />
                    </Popconfirm>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        ))
        : <Text type='secondary'>No next-year objectives set yet.</Text>}
      {!disabled && (
        <div style={{ marginBottom: 12 }}>
          <Button size='large' className='tti-btn-primary-grey' icon={<PlusOutlined />} onClick={() => onChange && onChange([...visibleObjectives, { priority: '', objective: '' }])}>Add Objective</Button>
        </div>
      )}
      {visibleObjectives.length > 0 && (
        <Text type={Math.abs(totalPriority - 100) < 0.01 ? 'success' : 'secondary'} strong>
          Total Priority: {totalPriority}% {Math.abs(totalPriority - 100) < 0.01 ? '✓' : ''}
        </Text>
      )}
    </Card>
  )
}
